/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/naming-convention */
import type { SDKRequestInterface } from '@cloudbase/adapter-interface'
import { Bot } from './bot'
import * as models from './models'
import * as types from './type'
import { readableStream2JsonObject } from './utils'
import { ICloudbaseConfig } from '@cloudbase/types'
import { langEvent } from '@cloudbase/utilities'

const { MODELS } = models

class AI {
  public bot: Bot
  public i18n: ICloudbaseConfig['i18n']
  defaultHeaders: { [x: string]: any }
  private _getBaseUrl?: () => string

  /** 动态获取 aiBaseUrl，支持 registerEndPointWithKey 后端点变更生效 */
  get aiBaseUrl(): string {
    return `${this._getBaseUrl ? this._getBaseUrl() : this.baseUrl}/ai`
  }

  /** 动态获取 aiBotBaseUrl，支持 registerEndPointWithKey 后端点变更生效 */
  get aiBotBaseUrl(): string {
    return `${this._getBaseUrl ? this._getBaseUrl() : this.baseUrl}/aibot`
  }

  constructor(
    private req: SDKRequestInterface,
    public baseUrl: string,
    i18n: ICloudbaseConfig['i18n'],
    getBaseUrl?: () => string,
  ) {
    this._getBaseUrl = getBaseUrl
    this.bot = new Bot(this.botRequest, this.aiBotBaseUrl)
    this.i18n = i18n

    langEvent.bus.on(langEvent.LANG_CHANGE_EVENT, (params) => {
      this.i18n = params.data?.i18n || this.i18n
    })
  }

  async handleResponseData(responseData: Promise<unknown> | ReadableStream<Uint8Array>, header?: Headers) {
    const GO_TO_AI_TEXT = `${this.i18n.t('请检查调用方式，或前往云开发 AI+ 首页查看文档')}：https://tcb.cloud.tencent.com/dev#/ai`

    const throwIfError = (json: unknown) => {
      if (typeof json === 'object' && json && 'code' in json && (json as any).code !== 'NORMAL') {
        throw new Error(`${this.i18n.t('AI+ 请求出错，错误码')}：${(json as any).code}，${this.i18n.t('错误信息')}：${
          (json as any).message
        }\n${GO_TO_AI_TEXT}\n${JSON.stringify(json, null, 2)}`,)
      }
    }

    // 非流式请求：
    // - 小程序/Web 适配器返回 Promise（has 'then'）
    // - Node 适配器已将 body 解析为普通对象（无 'then'、无 getReader）
    // 两种情况都按 JSON 校验错误码后原样返回
    if (typeof responseData === 'object' && responseData && typeof (responseData as any).getReader !== 'function') {
      const json = 'then' in responseData ? await responseData : responseData
      throwIfError(json)
      return responseData
    }

    // 判断 responseData 是否是 ReadableStream 实例（兼容 Node.js 适配器非流式场景已解析为 JSON 对象的情况）
    const isStream = typeof (responseData as any)?.getReader === 'function'

    if (!isStream) {
      // responseData 已经是解析后的 JSON 对象（如 Node.js 适配器非流式返回），直接检查错误码
      const json = responseData as unknown as Record<string, unknown>
      if (typeof json === 'object' && json && 'code' in json && json.code !== 'NORMAL') {
        throw new Error(`${this.i18n.t('AI+ 请求出错，错误码')}：${json.code}，${this.i18n.t('错误信息')}：${
          json.message
        }\n${GO_TO_AI_TEXT}\n${JSON.stringify(json, null, 2)}`,)
      }
      return responseData
    }

    // 流式请求，如果接口出错，会变成传 json 格式的响应，通过 header 判断格式
    if (header?.get?.('content-type')?.includes('application/json')) {
      const json = await readableStream2JsonObject(responseData as ReadableStream<Uint8Array>)
      throwIfError(json)
    }
    return responseData
  }

  createModel(
    model: string,
    options?: {
      defaultModelSubUrl?: string
    },
  ) {
    let simpleModel: types.SimpleChatModel

    const SimpleModelConstructor = MODELS[model]
    if (SimpleModelConstructor) {
      simpleModel = new SimpleModelConstructor(this.modelRequest, this.aiBaseUrl)
    } else {
      const subUrl = typeof options?.defaultModelSubUrl === 'string' ? options.defaultModelSubUrl : '/chat/completions'

      simpleModel = new models.DefaultSimpleModel(this.modelRequest, this.aiBaseUrl, `${model}${subUrl}`)
    }

    const reactModel = new models.ReactModel(simpleModel)
    return reactModel
  }

  registerModel(name: string, model: types.ChatModelConstructor) {
    if (MODELS[name] != null) {
      console.warn(`AI model ${name} already exists!`)
      return
    }
    MODELS[name] = model
  }

  createImageModel<T extends 'hunyuan-image' | (string & {})>(provider: T) {
    return new models.DefaultImageModel<T>(this.modelRequest, this.aiBaseUrl, provider)
  }

  modelRequest: types.ModelReq = async ({ url, data, headers, stream, timeout }) => {
    const fetchHeaders = {
      'Content-Type': 'application/json',
    }
    // 带 Accept: text/event-stream  请求头显式指定是 SSE 绕过 60s 的限制
    stream && Object.assign(fetchHeaders, { Accept: 'text/event-stream' })

    const { data: responseData, header } = (await this.req.fetch({
      method: 'post',
      headers: { [this.i18n?.LANG_HEADER_KEY]: this.i18n?.lang, ...fetchHeaders, ...headers },
      body: JSON.stringify(data),
      url: this.resolveUrl(url),
      stream,
      timeout,
    })) as { data: Promise<unknown> | ReadableStream<Uint8Array>; header?: Headers }

    return this.handleResponseData(responseData, header) as any
  }

  botRequest: types.BotReq = async ({ method, url, data = {}, headers, stream, timeout }) => {
    const resolvedUrl = this.resolveUrl(url)
    if (method === 'get') {
      return this.handleResponseData((await this.req.fetch({ url: `${resolvedUrl}?${objectToParam(data)}`, method, headers, stream, timeout })).data,)
    }

    const fetchHeaders = {
      'Content-Type': 'application/json',
    }
    // 带 Accept: text/event-stream  请求头显式指定是 SSE 绕过 60s 的限制
    stream && Object.assign(fetchHeaders, { Accept: 'text/event-stream' })

    const { data: responseData, header } = (await this.req.fetch({
      url: resolvedUrl,
      body: JSON.stringify(data),
      headers: {
        [this.i18n?.LANG_HEADER_KEY]: this.i18n?.lang,
        ...fetchHeaders,
        ...headers,
      },
      stream,
      method,
      timeout,
    })) as { data: Promise<unknown> | ReadableStream<Uint8Array>; header?: Headers }

    return this.handleResponseData(responseData, header) as any

    function objectToParam(obj: Object) {
      return Object.entries(obj)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
    }
  }

  registerFunctionTool(functionTool: types.FunctionTool) {
    if (models.toolMap.has(functionTool.name)) {
      console.warn(`AI function tool ${functionTool.name} already exists and will be overwritten!`)
    }
    models.toolMap.set(functionTool.name, functionTool.fn)
  }

  /** 动态替换 URL 中的 origin 部分，支持 registerEndPointWithKey 后生效 */
  private resolveUrl(url: string): string {
    if (!this._getBaseUrl) return url
    const currentBase = this._getBaseUrl()
    if (currentBase !== this.baseUrl && url.startsWith(this.baseUrl)) {
      return url.replace(this.baseUrl, currentBase)
    }
    return url
  }
}

export { AI }
